import { Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Emitter } from "pixi-particles";
import * as Constants from "../Data/Constants";
import { ElementData } from "../Data/ElementData";
import { Regions } from "../Data/Regions";
import * as SFX from "../Data/SoundEffectKeys";
import { WorldElement } from "../GameObjects/WorldElement";
import { BoxCollider } from "../Physics/BoxCollider";

export enum TicketColourSuffixes {
    RED = "-red",
    ORANGE = "-orange",
    YELLOW = "-yellow",
    GREEN = "-green",
    BLUE = "-blue",
    BLACK = "-black",
    WHITE = "",
}

export class TicketBooth extends WorldElement {
    private _ticket: PIXI.Sprite | undefined;
    private _confetti: Emitter | undefined;
    private _confettiContainer: PIXI.Container | undefined;

    private _ticketTween: TimelineLite | undefined;

    constructor(pWithTicket: boolean) {
        super();

        if (pWithTicket) {
            this.init(new ElementData(
                "ticket-booth", Constants.SPRITESHEET_TERRAIN_PREFIX + "common",
                false, true, new BoxCollider(75, 80, -2000, 2000),
                undefined, SFX.CASINO_RINGING,
            ));

            this._ticket = HLF.PixiUtils.makeSprite(Regions.activeData.ticket.asset, Regions.activeData.ticket.sheet);
            this._ticket.position.set(50, -425);
            this._ticket.anchor.set(0, 0.5);
            this._ticket.scale.set(0.65);
            this.addChild(this._ticket);

            this._ticketTween = new TimelineLite();
            this._ticketTween.to(this._ticket, 1.0, {
                y: -400,
                ease: Sine.easeInOut,
                repeat: -1,
                yoyo: true,
            });

            const shapes: string[] = [ "rect", "circle", "tri" ];
            const particles: any[] = new Array<any>();
            const sheet: PIXI.loaders.Resource = PIXI.loader.resources[Constants.SPRITESHEET_UI];
            const colours: TicketColourSuffixes[] = Regions.activeData.ticketColours;
            for (let i = 0; i < colours.length; ++i) {
                for (let j = 0; j < shapes.length; ++j) {
                    particles.push(sheet.textures!["particle-confetti-" + shapes[j] + colours[i]]);
                }
            }

            this._confettiContainer = new PIXI.Container();
            this._confettiContainer.position.copy(this._ticket.position);
            // this._confettiContainer.scale.set(0.7);
            this.addChild(this._confettiContainer);
            this._confetti = new Emitter(
                this._confettiContainer,
                particles,
                {
                    alpha: {
                        list: [
                            { value: 0.1, time: 0 },
                            { value: 0.8, time: 0.1 },
                            { value: 0.8, time: 0.8 },
                            { value: 0.1, time: 1 },
                        ],
                        isStepped: false,
                    },
                    speed: {
                        list: [
                            { value: 400, time: 0 },
                        ],
                        isStepped: false,
                        minimumSpeedMultiplier: 0.1,
                    },
                    acceleration: new PIXI.Point(0, 500),
                    // startRotation: { min: 180, max: 360 },
                    startRotation: { min: 135, max: 405 },
                    rotationSpeed: { min: 0, max: 0 },
                    lifetime: { min: 0.75, max: 1.0 },
                    frequency: 0.01,
                    spawnChance: 1,
                    particlesPerWave: 30,
                    emitterLifetime: 0.75,
                    maxParticles: 50,
                    pos: { x: 0, y: 0 },
                    addAtBack: false,
                    spawnType: "circle",
                    spawnCircle: { x: 0, y: 0, r: 30 },
                },
            );
            this._confetti.emit = false;
        }
        else {
            this.init(new ElementData("ticket-booth", Constants.SPRITESHEET_TERRAIN_PREFIX + "common", false, false));
        }
    }

    public update(pDeltaTime: number): void {
        if (this._confetti !== undefined) {
            this._confetti.update(pDeltaTime);
        }
    }

    public destroy(pOptions: boolean | PIXI.DestroyOptions = { children: true }): void {
        if (this._ticketTween !== undefined) { this._ticketTween.kill(); }
        super.destroy(pOptions);
    }

    public onTicketCollected(pDest: PIXI.Point): void {
        if (this._ticket === undefined) {
            return;
        }
        if (this._ticketTween !== undefined) { this._ticketTween.kill(); }
        const localDest: PIXI.Point = this.worldTransform.applyInverse(pDest);
        localDest.x += 40; // To account for balloon movement
        localDest.y -= 30; // Offset height so it reaches the center of the balloon portion
        this._ticketTween = new TimelineLite();
        this._ticketTween.to(this._ticket, 0.5, {
            x: localDest.x,
            y: localDest.y,
            rotation: Math.PI * 0.1,
            ease: Sine.easeInOut,
        }, 0);
        this._ticketTween.to(this._ticket.scale, 0.5, {
            x: 0,
            y: 0,
            ease: Sine.easeIn,
        }, 0);
        this._ticketTween.to(this._ticket, 0.25, {
            alpha: 0,
            ease: Sine.easeInOut,
        }, 0.25);
        this._ticketTween.call(() => {
            this._confettiContainer!.position.copy(this._ticket!.position);
            this._confetti!.emit = true;
        }, undefined, this, 0.5);
    }
}
